import {
  ARTICLES_SUBDOMAIN,
  BRAND_HINT_COOKIE,
  BRAND_HINT_MAX_AGE_SECONDS,
  HELP_DOCS_PATH,
  MAX_ARTICLE_SLUG_LENGTH,
} from '_constants/kb-links.constants'
import { hasControlChars } from './redirect-path.util'

const BRAND_SLUG_PATTERN = /^[a-z0-9-]+$/
const TRAVERSAL_SEGMENTS = ['.', '..']

const LOCAL_HOSTNAMES = ['localhost', '127.0.0.1']

export const isValidBrandSlug = (value: string | null | undefined): value is string =>
  !!value && BRAND_SLUG_PATTERN.test(value)

/**
 * KnowledgeArticle.slug is only `z.string().min(1).max(255)`, and the revision-approval flow
 * writes `proposedSlug` verbatim, so slugs are not guaranteed to be kebab-case. Anything that
 * survives as a single path segment is therefore accepted and percent-encoded on the way out,
 * rather than pattern-matched against a shape the backend never enforced.
 */
export const isValidArticleSlug = (value: string | null | undefined): value is string => {
  if (!value || value.length > MAX_ARTICLE_SLUG_LENGTH) {
    return false
  }
  if (hasControlChars(value)) {
    return false
  }
  if (TRAVERSAL_SEGMENTS.includes(value)) {
    return false
  }

  return !value.includes('/') && !value.includes('\\')
}

export const getRootDomain = (hostname: string = window.location.hostname): string | null => {
  // Dev hosts have no registrable parent to scope a cookie to, so callers degrade to the
  // brand picker rather than building a bogus `<brand>.articles.localhost` target.
  if (LOCAL_HOSTNAMES.includes(hostname) || hostname.endsWith('.localhost')) {
    return null
  }

  const parts = hostname.split('.')
  if (parts.length < 2) {
    return null
  }

  return parts.slice(-2).join('.')
}

export const isArticlesHost = (hostname: string = window.location.hostname): boolean =>
  hostname.split('.')[0] === ARTICLES_SUBDOMAIN

export const readBrandHint = (): string | null => {
  try {
    const match = document.cookie
      .split(';')
      .map((entry) => entry.trim())
      .find((entry) => entry.startsWith(`${BRAND_HINT_COOKIE}=`))

    if (!match) {
      return null
    }

    const value = decodeURIComponent(match.slice(BRAND_HINT_COOKIE.length + 1))
    return isValidBrandSlug(value) ? value : null
  } catch (_error) {
    return null
  }
}

export const writeBrandHint = (brand: string): void => {
  if (!isValidBrandSlug(brand)) {
    return
  }

  const rootDomain = getRootDomain()
  if (!rootDomain) {
    return
  }

  try {
    document.cookie = [
      `${BRAND_HINT_COOKIE}=${encodeURIComponent(brand)}`,
      `Domain=.${rootDomain}`,
      'Path=/',
      `Max-Age=${BRAND_HINT_MAX_AGE_SECONDS}`,
      'Secure',
      'SameSite=Lax',
    ].join('; ')
  } catch (_error) {
    // A blocked cookie jar degrades to the brand picker, which is a supported path.
  }
}

export const buildHelpDocsUrl = (brand: string, articleSlug: string): string | null => {
  const rootDomain = getRootDomain()
  if (!rootDomain || !isValidBrandSlug(brand) || !isValidArticleSlug(articleSlug)) {
    return null
  }

  // The host is always rebuilt from our own root domain and a slug-validated brand, so a
  // crafted ?b= value can never redirect off the Hapana estate.
  return `https://${brand}.${rootDomain}/${HELP_DOCS_PATH}/${encodeURIComponent(articleSlug)}`
}

export const extractArticleSlug = (pathname: string = window.location.pathname): string | null => {
  const trimmed = pathname.replace(/^\/+/, '').replace(/\/+$/, '')
  const withoutPrefix = trimmed.startsWith(`${HELP_DOCS_PATH}/`)
    ? trimmed.slice(HELP_DOCS_PATH.length + 1)
    : trimmed

  let decoded: string
  try {
    decoded = decodeURIComponent(withoutPrefix)
  } catch (_error) {
    return null
  }

  return isValidArticleSlug(decoded) ? decoded : null
}
